import { useMemo } from "react";
import { useMatches, useNavigation, useParams } from "react-router-dom";

import { resolveAccountSidebarNav } from "@/navigation/accountNavConfig";
import { resolveAppSidebarNav } from "@/navigation/appNavConfig";
import { resolveSettingsSidebarNav } from "@/navigation/settingsNavConfig";
import type { SidebarId, SidebarNavContext, SidebarNavSection } from "@/navigation/types";
import { resolveProjectSidebarNav } from "@/pages/Project/projectNavConfig";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import type { AppRouteHandle } from "@/types/routeSkeleton";

type SidebarResolver = (ctx: SidebarNavContext) => SidebarNavSection[];

const sidebarResolvers: Record<SidebarId, SidebarResolver> = {
  app: resolveAppSidebarNav,
  account: resolveAccountSidebarNav,
  project: resolveProjectSidebarNav,
  settings: resolveSettingsSidebarNav,
};

const DEFAULT_SIDEBAR_ID: SidebarId = "app";

function resolveSidebarIdFromMatches(matches: ReturnType<typeof useMatches>): SidebarId {
  for (let i = matches.length - 1; i >= 0; i--) {
    const handle = matches[i]?.handle as AppRouteHandle | undefined;
    if (handle?.sidebar) return handle.sidebar;
  }
  return DEFAULT_SIDEBAR_ID;
}

function resolveSidebarIdFromPathname(pathname: string): SidebarId {
  if (/^\/projects\/[^/]+/.test(pathname)) return "project";
  if (/^\/settings(\/|$)/.test(pathname)) return "settings";
  if (/^\/profile(\/|$)/.test(pathname)) return "account";
  return DEFAULT_SIDEBAR_ID;
}

function effectiveNavParams(
  navigation: ReturnType<typeof useNavigation>,
  routeParams: Record<string, string | undefined>,
): Record<string, string | undefined> {
  if (navigation.state !== "loading") return routeParams;

  const projectMatch = navigation.location.pathname.match(/^\/projects\/([^/]+)/);
  if (projectMatch) return { ...routeParams, projectId: projectMatch[1] };
  return routeParams;
}

function resolveSidebarSections(sidebarId: SidebarId, ctx: SidebarNavContext): SidebarNavSection[] {
  const sections = sidebarResolvers[sidebarId](ctx);
  if (sections.length > 0) return sections;
  if (sidebarId === DEFAULT_SIDEBAR_ID) return [];
  return sidebarResolvers[DEFAULT_SIDEBAR_ID](ctx);
}

export type SidebarNavState = {
  sidebarId: SidebarId;
  sections: SidebarNavSection[];
  /** True while project nav is resolving during a route transition. Settings/account stay rendered. */
  isLoading: boolean;
};

function isSidebarNavLoading(sidebarId: SidebarId, isNavigating: boolean): boolean {
  switch (sidebarId) {
    case "app":
    case "account":
    case "settings":
      return false;
    case "project":
      return isNavigating;
    default: {
      const _exhaustive: never = sidebarId;
      return _exhaustive;
    }
  }
}

export function useSidebarNavState(): SidebarNavState {
  const navigation = useNavigation();
  const matches = useMatches();
  const routeParams = useParams();
  const { can } = usePermissions();
  const { user } = useAuth();
  const showOrgBilling = Boolean(
    user?.organization && user.isOrgAdmin && !user.organization.billingExempt,
  );

  return useMemo(() => {
    const isNavigating = navigation.state === "loading";
    const activeSidebarId = resolveSidebarIdFromMatches(matches);
    const sidebarId = isNavigating
      ? resolveSidebarIdFromPathname(navigation.location.pathname)
      : activeSidebarId;

    const params = effectiveNavParams(navigation, routeParams);
    const ctx: SidebarNavContext = { params, can, showOrgBilling };
    const isLoading = isSidebarNavLoading(sidebarId, isNavigating);

    if (isLoading) {
      return { sidebarId, sections: [], isLoading: true };
    }

    return {
      sidebarId,
      sections: resolveSidebarSections(sidebarId, ctx),
      isLoading: false,
    };
  }, [navigation, matches, routeParams, can, showOrgBilling]);
}

export function useSidebarNav(): SidebarNavSection[] {
  return useSidebarNavState().sections;
}
